import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useSEO } from '../utils/seo';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { readResponse } from '../utils/checkout';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const COPY = {
  fr: {
    seoTitle: 'Mot de passe oublié — Le Placard à Doudous', seoDescription: 'Réinitialisez votre mot de passe.',
    sendError: 'Le lien n’a pas pu être envoyé. Réessayez dans un instant.', sent: 'Email envoyé !', error: 'Erreur de connexion',
    subtitle: 'Réinitialisation du mot de passe', sentText: 'Si cet email existe dans notre système, vous recevrez un lien de réinitialisation sous peu.',
    backToLogin: 'Retour à la connexion', intro: 'Saisissez votre email pour recevoir un lien de réinitialisation.', email: 'Email', emailPlaceholder: 'votre@email.fr',
    loading: 'Envoi...', submit: 'Recevoir le lien',
  },
  en: {
    seoTitle: 'Forgot password — Le Placard à Doudous', seoDescription: 'Reset your password.',
    sendError: 'The link couldn’t be sent. Please try again in a moment.', sent: 'Email sent!', error: 'Connection error',
    subtitle: 'Reset your password', sentText: 'If this email is in our system, you’ll receive a reset link shortly.',
    backToLogin: 'Back to login', intro: 'Enter your email to receive a reset link.', email: 'Email', emailPlaceholder: 'your@email.com',
    loading: 'Sending...', submit: 'Send me the link',
  },
};

export default function ForgotPassword() {
  const c = useCopy(COPY);
  const { locale, lp } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, locale }) });
      const data = await readResponse(res, c.sendError);
      setSent(true);
      toast.success(data.message || c.sent);
    } catch (err) { toast.error(err.message || c.error); }
    setLoading(false);
  };

  return (
    <div className="hero-mist noise min-h-screen flex items-center justify-center pt-20 pb-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-4">
        <div className="rounded-3xl bg-card p-8 shadow-ambient sm:p-10">
          <div className="text-center mb-8">
            <Link to={lp('/')} className="font-serif text-2xl text-foreground">Le Placard à Doudous</Link>
            <p className="font-sans text-sm text-[hsl(var(--muted-foreground))] mt-2">{c.subtitle}</p>
          </div>
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <span className="text-3xl">✉️</span>
              </div>
              <p className="font-serif text-lg text-foreground">{c.sent}</p>
              <p className="font-sans text-sm text-[hsl(var(--muted-foreground))]">{c.sentText}</p>
              <Button asChild variant="outline" className="rounded-xl font-sans text-sm w-full mt-4">
<Link to={lp('/connexion')}>{c.backToLogin}</Link>
</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="font-sans text-sm text-[hsl(var(--muted-foreground))] text-center">{c.intro}</p>
              <div className="space-y-2">
                <Label htmlFor="email" className="font-sans text-sm">{c.email}</Label>
                <Input id="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} required className="rounded-xl font-sans" placeholder={c.emailPlaceholder} />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground font-sans font-medium shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-ambient)] transition-[box-shadow] duration-200">
                {loading ? c.loading : c.submit}
              </Button>
              <p className="font-sans text-sm text-center">
                <Link to={lp('/connexion')} className="text-muted-foreground hover:text-foreground transition-colors duration-200">{c.backToLogin}</Link>
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
